import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { tokens, greenRoomPalette } from '../tokens';
import useIsDesktop from '../useIsDesktop';
import api from '../api';

const p = greenRoomPalette;
const serif = tokens.fontDisplay;
const body = tokens.fontBody;
const mono = tokens.fontCode;

const PROMPTS = [
  'What went right that nobody acknowledged?',
  "What's still sitting with you?",
  'What would you tell the version of yourself that started this job?',
  'What do you need to let go of?',
];

const PRODUCTION_TYPES = [
  'concert', 'corporate', 'festival', 'film / tv', 'musical',
  'one-night', 'opera', 'play', 'residency', 'tour', 'other',
];

const formatDate = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
};

function PencilGlyph({ color = 'currentColor', size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" style={{ flexShrink: 0, display: 'block' }}>
      <path d="M3 17 L5.5 16 L15 6.5 L13.5 5 L4 14.5 Z M12 4.5 L15.5 8 L17 6.5 L13.5 3 Z"
        fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function GreenRoom() {
  const isDesktop = useIsDesktop();
  const [mode, setMode] = useState('lobby');
  const [prodType, setProdType] = useState(null);
  const [answers, setAnswers] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [debriefs, setDebriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/debriefs')
      .then(res => setDebriefs(res.data.debriefs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError('');
    if (!prodType) { setError('Select a production type'); return; }

    const responses = PROMPTS
      .filter(pr => answers[pr] && answers[pr].trim())
      .map(pr => ({ prompt: pr, answer: answers[pr] }));

    if (responses.length === 0) { setError('Answer at least one prompt'); return; }

    setSubmitting(true);
    try {
      const { data } = await api.post('/debriefs', { productionType: prodType, responses });
      setDebriefs(prev => [data.debrief, ...prev]);
      setMode('lobby');
      setProdType(null);
      setAnswers({});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save debrief');
    }
    setSubmitting(false);
  };

  const handleShare = async (debriefId) => {
    if (!window.confirm('Share this debrief to The Stage? It will be visible under your alias.')) return;
    try {
      const { data } = await api.post(`/debriefs/${debriefId}/share`);
      setDebriefs(prev => prev.map(d => d._id === debriefId ? data.debrief : d));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to share');
    }
  };

  const handleDelete = async (debriefId) => {
    if (!window.confirm('Delete this debrief? This cannot be undone.')) return;
    try {
      await api.delete(`/debriefs/${debriefId}`);
      setDebriefs(prev => prev.filter(d => d._id !== debriefId));
      if (expanded === debriefId) setExpanded(null);
    } catch {}
  };

  const privateLabel = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: serif, fontStyle: 'italic', fontSize: 13, color: p.inkLow,
    }}>
      <svg width="10" height="12" viewBox="0 0 10 12" fill="none" aria-hidden="true" style={{ opacity: 0.7 }}>
        <rect x="1" y="5" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1" fill="none"/>
        <path d="M3 5V3.5a2 2 0 014 0V5" stroke="currentColor" strokeWidth="1" fill="none"/>
      </svg>
      private
    </span>
  );

  const paperBg = (
    <div aria-hidden="true" style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      background: `radial-gradient(ellipse at 20% 0%, ${p.paperDeep}55, transparent 55%),
                   radial-gradient(ellipse at 90% 100%, ${p.paperDeep}33, transparent 60%)`,
    }} />
  );

  /* ── Desktop: two-column layout ── */
  if (isDesktop) {
    return (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        background: p.paper, color: p.ink,
        fontFamily: body, position: 'relative',
      }}>
        {paperBg}
        <Navbar />

        <main style={{ flex: 1, overflowY: 'auto', position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: '48px 40px 80px' }}>
            {mode === 'lobby' && (
              <DesktopLobby
                debriefs={debriefs} expanded={expanded} setExpanded={setExpanded}
                loading={loading}
                onStart={() => { setMode('writing'); setError(''); }}
                onShare={handleShare} onDelete={handleDelete}
              />
            )}
            {mode === 'writing' && (
              <WritingView
                prodType={prodType} setProdType={setProdType}
                answers={answers} setAnswers={setAnswers}
                error={error} submitting={submitting}
                onCancel={() => { setMode('lobby'); setProdType(null); setAnswers({}); setError(''); }}
                onSave={handleSave}
              />
            )}
          </div>
        </main>
      </div>
    );
  }

  /* ── Mobile: single column ── */
  return (
    <div style={{
      position: 'relative', minHeight: '100vh',
      background: p.paper,
      paddingBottom: 120,
      animation: `gl-fade 800ms ${tokens.ease}`,
    }}>
      <Navbar />
      {paperBg}

      <div style={{ maxWidth: 680, margin: '0 auto' }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 15,
        padding: '14px 22px 12px',
        background: `linear-gradient(to bottom, ${p.paper} 85%, transparent)`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{
          fontFamily: mono, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: tokens.accent,
        }}>The Green Room</span>
        {privateLabel}
      </div>

      {mode === 'lobby' && (
        <LobbyView
          debriefs={debriefs} expanded={expanded} setExpanded={setExpanded}
          loading={loading}
          onStart={() => { setMode('writing'); setError(''); }}
          onShare={handleShare} onDelete={handleDelete}
        />
      )}

      {mode === 'writing' && (
        <WritingView
          prodType={prodType} setProdType={setProdType}
          answers={answers} setAnswers={setAnswers}
          error={error} submitting={submitting}
          onCancel={() => { setMode('lobby'); setProdType(null); setAnswers({}); setError(''); }}
          onSave={handleSave}
        />
      )}
      </div>{/* end max-width wrapper */}
    </div>
  );
}

function LobbyView({ debriefs, expanded, setExpanded, loading, onStart, onShare, onDelete }) {
  return (
    <>
      {/* Invitation */}
      <div style={{ padding: '32px 30px 0', position: 'relative', zIndex: 2, animation: `gl-fade-up 700ms ease both` }}>
        <p style={{
          fontFamily: serif, fontStyle: 'italic', fontSize: 15, color: p.inkLow,
          margin: 0, letterSpacing: '0.01em',
        }}>
          Welcome back.
        </p>
        <h1 style={{
          fontFamily: serif, fontSize: 34, lineHeight: 1.22,
          color: p.ink, margin: '12px 0 0', fontWeight: 400,
          letterSpacing: '-0.01em',
        }}>
          Take off your shoes.<br/>
          <span style={{ fontStyle: 'italic', color: p.inkMid }}>Put the show down.</span>
        </h1>
        <p style={{
          fontFamily: body, fontSize: 14, lineHeight: 1.65,
          color: p.inkMid, margin: '22px 0 0', maxWidth: 320,
        }}>
          This page is yours. Nobody else sees what you write here unless you choose to share it. Four prompts. As many or as few as you want.
        </p>
      </div>

      {/* New journal button */}
      <div style={{ padding: '40px 30px 0', position: 'relative', zIndex: 2 }}>
        <button onClick={onStart} style={{
          background: 'transparent', border: 'none', padding: '18px 0',
          borderTop: `1px solid ${p.hair}`, borderBottom: `1px solid ${p.hair}`,
          width: '100%', textAlign: 'left', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 14,
          fontFamily: serif, fontStyle: 'italic', fontSize: 19,
          color: p.inkMid, letterSpacing: '0.005em',
          transition: 'color 240ms ease',
        }}
          onMouseEnter={e => e.currentTarget.style.color = p.ink}
          onMouseLeave={e => e.currentTarget.style.color = p.inkMid}
        >
          <PencilGlyph color={p.mark} size={16} />
          Open a new journal page...
        </button>
      </div>

      {/* Earlier pages */}
      <div style={{ padding: '40px 30px 0', position: 'relative', zIndex: 2 }}>
        {loading ? (
          <div style={{ padding: '40px 0', fontFamily: serif, fontStyle: 'italic', fontSize: 15, color: p.inkLow }}>
            Loading...
          </div>
        ) : debriefs.length === 0 ? (
          <div style={{
            padding: '40px 0',
            fontFamily: serif, fontStyle: 'italic', fontSize: 18,
            color: p.inkLow,
          }}>
            No pages yet.<br/>
            <span style={{ fontSize: 15 }}>Start one after your next show.</span>
          </div>
        ) : (
          <>
            <div style={{
              fontFamily: mono, fontSize: 10, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: p.inkLow, marginBottom: 22,
            }}>Earlier pages</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              {debriefs.map((d, i) => {
                const isOpen = expanded === d._id;
                const preview = d.responses && d.responses.length > 0
                  ? d.responses[0].answer
                  : '';
                return (
                  <article key={d._id} style={{
                    animation: `gl-fade-up 600ms ease ${i * 90}ms both`,
                    padding: '22px 24px',
                    background: p.paperDeep,
                    border: `1px solid ${p.hair}`,
                    borderRadius: 2,
                    boxShadow: `0 1px 0 ${p.hair}, 0 14px 28px -20px rgba(0,0,0,0.6)`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
                      <div style={{
                        fontFamily: mono, fontSize: 10, letterSpacing: '0.2em',
                        textTransform: 'uppercase', color: p.inkLow,
                      }}>{formatDate(d.createdAt)}</div>
                      <div style={{
                        fontFamily: serif, fontStyle: 'italic', fontSize: 13,
                        color: p.mark, letterSpacing: '0.01em',
                      }}>{d.productionType}</div>
                    </div>
                    {preview && (
                      <p style={{
                        fontFamily: serif, fontStyle: 'italic', fontSize: 17, lineHeight: 1.5,
                        color: p.ink, margin: '14px 0 0',
                      }}>
                        {preview.length > 160 ? preview.slice(0, 160) + '...' : preview}
                      </p>
                    )}
                    {isOpen && (
                      <div style={{
                        marginTop: 18, paddingTop: 16,
                        borderTop: `1px solid ${p.hair}`,
                        display: 'flex', flexDirection: 'column', gap: 20,
                        animation: `gl-fade-up 360ms ease both`,
                      }}>
                        {d.responses.map((r, ri) => (
                          <div key={ri}>
                            <div style={{
                              fontFamily: serif, fontSize: 14, fontStyle: 'italic',
                              color: p.inkLow, marginBottom: 6, letterSpacing: '0.01em',
                            }}>{r.prompt}</div>
                            <div style={{
                              fontFamily: body, fontSize: 14, lineHeight: 1.65,
                              color: p.inkMid, whiteSpace: 'pre-wrap',
                            }}>{r.answer}</div>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex', gap: 22, marginTop: 6,
                          fontFamily: mono, fontSize: 10, letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                        }}>
                          {!d.sharedToStage && (
                            <button
                              onClick={() => onShare(d._id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.mark, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}
                            >Share to The Stage</button>
                          )}
                          {d.sharedToStage && d.sharedPostId && (
                            <Link to={`/post/${d.sharedPostId}`} style={{
                              color: p.mark, fontFamily: 'inherit', fontSize: 'inherit',
                              letterSpacing: 'inherit', textTransform: 'inherit', textDecoration: 'none',
                            }}>View on The Stage</Link>
                          )}
                          <button
                            onClick={() => onDelete(d._id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.inkLow, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}
                          >Delete page</button>
                        </div>
                      </div>
                    )}
                    <button onClick={() => setExpanded(isOpen ? null : d._id)} style={{
                      marginTop: 14, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: mono, fontSize: 10, letterSpacing: '0.2em',
                      textTransform: 'uppercase', color: p.inkLow,
                    }}>
                      {isOpen ? '\u2014 Close' : '+ Open'}
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function DesktopLobby({ debriefs, expanded, setExpanded, loading, onStart, onShare, onDelete }) {
  return (
    <>
      {/* Invitation */}
      <div style={{ maxWidth: 560 }}>
        <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 15, color: p.inkLow, margin: 0 }}>Welcome back.</p>
        <h1 style={{
          fontFamily: serif, fontSize: 42, lineHeight: 1.2,
          color: p.ink, margin: '12px 0 0', fontWeight: 400,
        }}>
          Take off your shoes.<br/>
          <span style={{ fontStyle: 'italic', color: p.inkMid }}>Put the show down.</span>
        </h1>
        <p style={{
          fontFamily: body, fontSize: 15, lineHeight: 1.65,
          color: p.inkMid, margin: '22px 0 0',
        }}>
          This page is yours. Nobody else sees what you write here unless you choose to share it. Four prompts. As many or as few as you want.
        </p>
      </div>

      {/* Two-col: new page button + earlier pages */}
      <div style={{
        marginTop: 48,
        display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 60,
        alignItems: 'start',
      }}>
        <button onClick={onStart} style={{
          background: p.paperDeep, border: `1px solid ${p.hair}`,
          padding: '36px 28px', cursor: 'pointer', width: '100%',
          textAlign: 'left',
          display: 'flex', flexDirection: 'column', gap: 16,
          transition: 'border-color 240ms ease',
          position: 'sticky', top: 20,
        }}
          onMouseEnter={e => e.currentTarget.style.borderColor = p.mark + '88'}
          onMouseLeave={e => e.currentTarget.style.borderColor = p.hair}
        >
          <PencilGlyph color={p.mark} size={22} />
          <div style={{
            fontFamily: serif, fontStyle: 'italic', fontSize: 24,
            color: p.ink, letterSpacing: '0.005em', lineHeight: 1.3,
          }}>Open a new journal page...</div>
          <div style={{
            fontFamily: mono, fontSize: 10, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: p.mark,
          }}>Begin →</div>
        </button>

        <div>
          {loading ? (
            <div style={{ padding: '40px 0', fontFamily: serif, fontStyle: 'italic', fontSize: 15, color: p.inkLow }}>Loading...</div>
          ) : debriefs.length === 0 ? (
            <div style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 18, color: p.inkLow }}>
              No pages yet.<br/><span style={{ fontSize: 15 }}>Start one after your next show.</span>
            </div>
          ) : (
            <>
              <div style={{
                fontFamily: mono, fontSize: 11, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: p.inkLow, marginBottom: 22,
              }}>Earlier pages</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {debriefs.map((d, i) => {
                  const isOpen = expanded === d._id;
                  const preview = d.responses?.length > 0 ? d.responses[0].answer : '';
                  return (
                    <article key={d._id} style={{
                      animation: `gl-fade-up 600ms ease ${i * 90}ms both`,
                      padding: '24px 26px', background: p.paperDeep,
                      border: `1px solid ${p.hair}`,
                      boxShadow: `0 1px 0 ${p.hair}, 0 14px 28px -20px rgba(0,0,0,0.6)`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
                        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: p.inkLow }}>{formatDate(d.createdAt)}</div>
                        <div style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 13, color: p.mark }}>{d.productionType}</div>
                      </div>
                      {preview && (
                        <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 18, lineHeight: 1.5, color: p.ink, margin: '14px 0 0' }}>
                          {preview.length > 160 ? preview.slice(0, 160) + '...' : preview}
                        </p>
                      )}
                      {isOpen && (
                        <div style={{
                          marginTop: 20, paddingTop: 18, borderTop: `1px solid ${p.hair}`,
                          display: 'flex', flexDirection: 'column', gap: 22,
                          animation: `gl-fade-up 360ms ease both`,
                        }}>
                          {d.responses.map((r, ri) => (
                            <div key={ri}>
                              <div style={{ fontFamily: serif, fontSize: 14, fontStyle: 'italic', color: p.inkLow, marginBottom: 6 }}>{r.prompt}</div>
                              <div style={{ fontFamily: body, fontSize: 14, lineHeight: 1.7, color: p.inkMid, whiteSpace: 'pre-wrap' }}>{r.answer}</div>
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: 22, marginTop: 6, fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                            {!d.sharedToStage && (
                              <button onClick={() => onShare(d._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.mark, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>Share to The Stage</button>
                            )}
                            {d.sharedToStage && d.sharedPostId && (
                              <Link to={`/post/${d.sharedPostId}`} style={{ color: p.mark, fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', textDecoration: 'none' }}>View on The Stage</Link>
                            )}
                            <button onClick={() => onDelete(d._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.inkLow, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>Delete page</button>
                          </div>
                        </div>
                      )}
                      <button onClick={() => setExpanded(isOpen ? null : d._id)} style={{
                        marginTop: 16, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontFamily: mono, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: p.inkLow,
                      }}>{isOpen ? '\u2014 Close' : '+ Open'}</button>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function WritingView({ prodType, setProdType, answers, setAnswers, error, submitting, onCancel, onSave }) {
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div style={{ padding: '30px 30px 0', position: 'relative', zIndex: 2, animation: `gl-fade-up 500ms ease both` }}>
      {/* Datestamp */}
      <div style={{
        fontFamily: mono, fontSize: 10, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: p.inkLow, marginBottom: 8,
      }}>{dateStr}</div>
      <h2 style={{
        fontFamily: serif, fontStyle: 'italic', fontSize: 22,
        color: p.ink, margin: 0, letterSpacing: '0.005em', fontWeight: 400,
      }}>
        A new journal page.
      </h2>

      {error && (
        <div style={{
          marginTop: 20, padding: '12px 16px',
          background: `${p.mark}15`, border: `1px solid ${p.mark}40`,
          color: p.mark, fontFamily: body, fontSize: 14, borderRadius: 2,
        }}>{error}</div>
      )}

      {/* Production type picker */}
      <div style={{ marginTop: 32 }}>
        <p style={{
          fontFamily: serif, fontStyle: 'italic', fontSize: 16,
          color: p.inkMid, margin: '0 0 12px',
        }}>What kind of production?</p>
        <div style={{
          display: 'flex', flexWrap: 'wrap', rowGap: 8, columnGap: 14,
          fontFamily: serif, fontSize: 16, fontStyle: 'italic',
        }}>
          {PRODUCTION_TYPES.map((t, i) => {
            const active = prodType === t;
            return (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 0 }}>
                {i > 0 && <span style={{ color: p.inkDim, marginRight: 14 }}>/</span>}
                <button onClick={() => setProdType(t)} style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 'inherit', fontStyle: 'inherit',
                  color: active ? p.mark : p.inkLow,
                  textDecoration: active ? 'underline' : 'none',
                  textDecorationThickness: '1px', textUnderlineOffset: 3,
                  opacity: prodType && !active ? 0.45 : 1,
                  transition: 'all 240ms ease',
                }}>{t}</button>
              </span>
            );
          })}
        </div>
      </div>

      {/* Prompts */}
      <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 32 }}>
        {PROMPTS.map((prompt, i) => (
          <div key={i}>
            <label style={{
              display: 'block',
              fontFamily: serif, fontStyle: 'italic', fontSize: 18, lineHeight: 1.45,
              color: p.inkMid, marginBottom: 10, letterSpacing: '0.005em',
            }}>{prompt}</label>
            <textarea
              value={answers[prompt] || ''}
              onChange={e => setAnswers(a => ({ ...a, [prompt]: e.currentTarget.value }))}
              placeholder="..."
              rows={3}
              maxLength={5000}
              style={{
                width: '100%', background: 'transparent', resize: 'none', outline: 'none',
                border: 'none', borderBottom: `1px solid ${p.hair}`,
                padding: '6px 0 10px',
                fontFamily: body, fontSize: 15, lineHeight: 1.7, color: p.ink,
                boxSizing: 'border-box',
              }}
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.max(56, e.target.scrollHeight) + 'px'; }}
            />
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ marginTop: 48, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={onCancel} style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontFamily: mono, fontSize: 11, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: p.inkLow,
        }}>Close page</button>
        <button onClick={onSave} disabled={submitting} style={{
          background: 'transparent', border: `1px solid ${p.mark}`,
          padding: '12px 22px', cursor: 'pointer',
          fontFamily: mono, fontSize: 11, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: p.mark,
          opacity: submitting ? 0.5 : 1,
        }}>{submitting ? 'Saving...' : 'Save this page'}</button>
      </div>
    </div>
  );
}
